import { useState, type FormEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type AporteGrupo, type Caja, type FiduciantePublico, type Grupo } from "@/lib/api";
import type { FormAporte } from "@/lib/aportes";
import { hayVariasCajas, opcionesCaja } from "@/lib/cajas";
import {
  aPayloadAporteGrupo,
  aPayloadGrupo,
  agregarLinea,
  cambiarLinea,
  formAporteGrupo,
  formGrupo,
  opcionesMiembro,
  quitarLinea,
  repartirGrupo,
  textoSuma,
  validarAporteGrupo,
  validarGrupo,
  type ErroresAporteGrupo,
  type ErroresGrupo,
  type FormGrupo,
  type LineaGrupo,
  type PayloadAporteGrupo,
  type PayloadGrupo,
} from "@/lib/grupos";

type Dialogo =
  | { tipo: "grupo"; grupo: Grupo | null }
  | { tipo: "borrarGrupo"; grupo: Grupo }
  | { tipo: "aporte"; aporte: AporteGrupo; grupo: Grupo }
  | { tipo: "borrarAporte"; aporte: AporteGrupo }
  | null;

const SIN_ERRORES_GRUPO: ErroresGrupo = {};
const SIN_ERRORES_APORTE: ErroresAporteGrupo = {};

/**
 * Lógica de Grupos (sin JSX; Mati, 2026-10-19): la empresa de varios fiduciantes con su % fijo. Una tarjeta por grupo
 * con lo que puso cada uno (con el grupo y por su cuenta) y su saldo, y los aportes del grupo. El alta de un aporte del
 * grupo está en Aportes ("Un grupo"); acá se corrige y se borra. Admin y contador leen; sólo el admin escribe.
 */
export function useGruposTab() {
  const { fid, membresia } = useContextoApp();
  const base = `/api/fideicomisos/${fid}`;
  const esAdmin = membresia.rol === "admin";

  const grupos = useApiResource<Grupo[]>(`${base}/grupos`);
  const aportes = useApiResource<AporteGrupo[]>(`${base}/aportes-grupo`);
  const fiduciantes = useApiResource<FiduciantePublico[]>(esAdmin ? `${base}/fiduciantes` : null);
  const cajas = useApiResource<Caja[]>(esAdmin ? `${base}/cajas` : null);

  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [form, setForm] = useState<FormGrupo>(() => formGrupo());
  const [formAporte, setFormAporte] = useState<FormAporte | null>(null);
  const [mostrarErrores, setMostrarErrores] = useState(false);

  const accionGrupo = useAsyncAction((id: number | null, datos: PayloadGrupo) =>
    id === null ? api.post<Grupo>(`${base}/grupos`, datos) : api.patch<Grupo>(`${base}/grupos/${id}`, datos),
  );
  const accionBorrarGrupo = useAsyncAction((id: number) => api.delete<void>(`${base}/grupos/${id}`).then(() => true));
  const accionAporte = useAsyncAction((id: number, datos: PayloadAporteGrupo) => api.patch<AporteGrupo>(`${base}/aportes-grupo/${id}`, datos));
  const accionBorrarAporte = useAsyncAction((id: number) => api.delete<void>(`${base}/aportes-grupo/${id}`).then(() => true));

  const recargar = () => {
    grupos.recargar();
    aportes.recargar();
  };
  const cerrar = () => setDialogo(null);

  const grupoEditado = dialogo?.tipo === "grupo" ? dialogo.grupo : null;
  // Con aportes, los miembros y sus % quedan fijos: sólo se cambia el nombre (el backend responde 409 si no).
  const miembrosFijos = grupoEditado?.tiene_aportes ?? false;
  const erroresGrupo = miembrosFijos ? { nombre: validarGrupo(form).nombre } : validarGrupo(form);
  const grupoDelAporte = dialogo?.tipo === "aporte" ? dialogo.grupo : null;
  const erroresAporte = formAporte ? validarAporteGrupo(formAporte, grupoDelAporte) : SIN_ERRORES_APORTE;

  const abrirGrupo = (g: Grupo | null) => {
    setForm(formGrupo(g ?? undefined));
    setMostrarErrores(false);
    accionGrupo.limpiarError();
    setDialogo({ tipo: "grupo", grupo: g });
  };
  const guardarGrupo = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (dialogo?.tipo !== "grupo" || Object.values(erroresGrupo).some(Boolean)) return;
    if (await accionGrupo.run(dialogo.grupo?.id ?? null, aPayloadGrupo(form, !miembrosFijos))) {
      cerrar();
      recargar();
    }
  };
  const abrirAporte = (a: AporteGrupo) => {
    const g = (grupos.data ?? []).find((x) => x.id === a.grupo_id);
    if (!g) return;
    setFormAporte(formAporteGrupo(a));
    setMostrarErrores(false);
    accionAporte.limpiarError();
    setDialogo({ tipo: "aporte", aporte: a, grupo: g });
  };
  const guardarAporte = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (dialogo?.tipo !== "aporte" || !formAporte || Object.keys(erroresAporte).length > 0) return;
    if (await accionAporte.run(dialogo.aporte.id, aPayloadAporteGrupo(formAporte, null))) {
      cerrar();
      recargar();
    }
  };
  const confirmarBorrar = async () => {
    const ok =
      dialogo?.tipo === "borrarGrupo"
        ? await accionBorrarGrupo.run(dialogo.grupo.id)
        : dialogo?.tipo === "borrarAporte"
          ? await accionBorrarAporte.run(dialogo.aporte.id)
          : false;
    if (ok) {
      cerrar();
      recargar();
    }
  };

  const faltan = !grupos.data || !aportes.data;
  const listaGrupos = grupos.data ?? [];
  return {
    esAdmin,
    cargando: faltan && (grupos.loading || aportes.loading),
    errorCarga: faltan ? (grupos.error?.message ?? aportes.error?.message ?? null) : null,
    reintentar: recargar,
    grupos: listaGrupos,
    aportesDe: (g: Grupo) => (aportes.data ?? []).filter((a) => a.grupo_id === g.id),
    dialogo,
    cerrar,
    // grupo
    abrirGrupo,
    abrirBorrarGrupo: (g: Grupo) => {
      accionBorrarGrupo.limpiarError();
      setDialogo({ tipo: "borrarGrupo", grupo: g });
    },
    form,
    miembrosFijos,
    cambiarNombre: (nombre: string) => setForm((f) => ({ ...f, nombre })),
    cambiarLinea: (i: number, campo: keyof LineaGrupo, valor: string) => setForm((f) => cambiarLinea(f, i, campo, valor)),
    agregarLinea: () => setForm(agregarLinea),
    quitarLinea: (i: number) => setForm((f) => quitarLinea(f, i)),
    opcionesMiembro: (i: number) => opcionesMiembro(fiduciantes.data ?? [], listaGrupos, form, i, grupoEditado?.id ?? null),
    textoSuma: textoSuma(form),
    erroresGrupo: mostrarErrores ? erroresGrupo : SIN_ERRORES_GRUPO,
    guardarGrupo,
    guardandoGrupo: accionGrupo.loading,
    errorGuardarGrupo: accionGrupo.error,
    // aporte del grupo
    abrirAporte,
    abrirBorrarAporte: (a: AporteGrupo) => {
      accionBorrarAporte.limpiarError();
      setDialogo({ tipo: "borrarAporte", aporte: a });
    },
    formAporte,
    cambiarAporte: <K extends keyof FormAporte>(campo: K, valor: FormAporte[K]) => setFormAporte((f) => (f ? { ...f, [campo]: valor } : f)),
    opcionesCaja: hayVariasCajas(cajas.data ?? []) ? opcionesCaja(cajas.data ?? [], dialogo?.tipo === "aporte" ? dialogo.aporte.caja_id : null) : null,
    vistaPrevia: formAporte && grupoDelAporte ? repartirGrupo(formAporte.monto, grupoDelAporte) : null,
    erroresAporte: mostrarErrores ? erroresAporte : SIN_ERRORES_APORTE,
    guardarAporte,
    guardandoAporte: accionAporte.loading,
    errorGuardarAporte: accionAporte.error,
    // borrar (grupo o aporte)
    confirmarBorrar,
    borrando: accionBorrarGrupo.loading || accionBorrarAporte.loading,
    errorBorrar: accionBorrarGrupo.error ?? accionBorrarAporte.error,
  };
}
